import create from 'zustand';
import { inventoryService } from '../services';
import { debounce } from 'lodash';
import { Filter } from '../@types/inventory';
import { v4 as uuidv4 } from 'uuid';
import { Inventory, Variation } from '../@types/inventory';
import { IInventory, IPaginationMeta } from '../@types';

interface InventoryStore {
  inventories: IInventory[];
  variations: [];
  isLoading: boolean;
  error: string | null;
  pagination: IPaginationMeta;
  addInventory: (inventory: Omit<IInventory, 'id' | 'variations'>) => void;
  updateInventory: (id: string, updatedFields: Partial<IInventory>) => void;
  removeInventory: (id: string) => void;
  addVariation: (inventoryId: string, variation: Omit<Variation, 'id'>) => void;
  createVariation: (data: {variantName: string, types: string[]}) => void;
  updateVariation: (inventoryId: string, variationId: string, updatedFields: Partial<Variation>) => void;
  removeVariation: (inventoryId: string, variationId: string) => void;
  fetchInventory: () => Promise<void>;
  fetchVariation: () => Promise<void>;
  fetchData: () => Promise<void> | undefined;
  fetchMoreData: () => Promise<void>;
  setSearchText: (text: string) => void;
  handleSort: (text: string) => void;
  setFilter: (filter: Filter) => void;
  searchText: string;
  range: string;
  filter: Filter;
  inventory: IInventory[];
  fetchInventoryById: (id: string) => Promise<void>;
}

export const useInventoryStore = create<InventoryStore>((set, get) => ({
  inventories: [],
  variations: [],
  inventory: [],
  isLoading: false,
  error: null,
  searchText: '',
  range: '',
  filter: {
    stockStatus: '',
    variantStatus: '',
  },
  pagination: {
    cursor: null,
    backCursor: null,
    hasMoreData: false,
    limit: undefined,
    count: undefined,
  },
  fetchData: debounce(async () => {
    set((prevState: InventoryStore) => {
      return {
        ...prevState,
        isLoading: true,
      };
    });

    const { searchText, filter, range } = get();

    try {
      let query = `/inventories`;
      if (
        range.length > 0 ||
        searchText.length > 0 ||
        ('stockStatus' in filter && filter.stockStatus.length > 0) ||
        ('variantStatus' in filter && filter.variantStatus !== null)
      ) {
        query += `?`;
        if (searchText.length > 0) query += `search=${searchText}&`;
        if (range.length > 0) query += `range=${range}&`;
        if ('stockStatus' in filter && filter.stockStatus.length > 0)
          query += `stockStatus=${filter.stockStatus.toLowerCase()}&`;
        if ('variantStatus' in filter && filter.variantStatus?.length > 0)
          query += `variantStatus=${filter.variantStatus}&`;
      }

      const res = await inventoryService.get(query);

      set((prevState: InventoryStore) => {
        return {
          ...prevState,
          inventory: res.data.data,
          pagination: res.data.meta,
          isLoading: false,
        };
      });
    } catch (e: any) {
      set((prevState: InventoryStore) => {
        return {
          ...prevState,
          isLoading: false,
          error: e.message,
        };
      });
    }
  }, 300),
  fetchInventory: async () => {
    set((prevState: InventoryStore) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      const res = await inventoryService.get(`/inventories`);
      set((prevState: InventoryStore) => ({
        ...prevState,
        inventory: res.data.data,
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      console.log(e.response.data);
      set((prevState: InventoryStore) => ({
        ...prevState,
        isLoading: false,
        error: e.message,
      }));
    }
  },
  fetchVariation: async () => {
    set((prevState: InventoryStore) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      const res = await inventoryService.get('/inventories/variant');
      set((prevState: InventoryStore) => ({
        ...prevState,
        variations: res.data,
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      console.log(e.response.data);
      set((prevState: InventoryStore) => ({
        ...prevState,
        isLoading: false,
        error: e.message,
      }));
    }
  },
  fetchMoreData: async () => {
    if (!get().pagination.hasMoreData || get().isLoading) return;
    set((prevState: InventoryStore) => ({
      ...prevState,
      isLoading: true,
    }));
    const cursor = get().pagination.cursor;
    try {
      const res = await inventoryService.get(`/inventories?cursor=${cursor}`);
      set((prevState: InventoryStore) => ({
        ...prevState,
        inventory: [...prevState.inventory, ...res.data.data],
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      console.log(e.response.data);
      set((prevState: InventoryStore) => ({
        ...prevState,
        isLoading: false,
        error: e.message,
      }));
    }
  },
  setSearchText: (text: string) => {
    set({ searchText: text });
  },
  handleSort: (text: string) => {
    set({ range: text });
  },
  setFilter: (filter: Filter) => {
    set({ filter: filter as Filter });
  },
  addInventory: (inventory: Omit<IInventory, 'id' | 'variations'>) => {
    const newInventory: IInventory = { ...inventory, id: uuidv4(), variations: [] };
    set((state: InventoryStore) => ({
      ...state,
      inventories: [...state.inventories, newInventory],
    }));
  },

  updateInventory: (id: string, updatedFields: Partial<IInventory>) => {
    set((state: InventoryStore) => ({
      ...state,
      inventories: state.inventories.map((inventory) =>
        inventory.id === id ? { ...inventory, ...updatedFields } : inventory,
      ),
    }));
  },

  removeInventory: (id) => {
    console.log('delete');
    set((state) => ({ inventories: state.inventories.filter((inventory) => inventory.id !== id) }));
  },
  addVariation: (inventoryId, variation) => {
    const newVariation = { ...variation, id: uuidv4() };
    set((state) => ({
      inventories: state.inventories.map((inventory) =>
        inventory.id === inventoryId
          ? { ...inventory, variations: [...inventory.variations, newVariation] }
          : inventory,
      ),
    }));
  },
  updateVariation: (inventoryId, variationId, updatedFields) => {
    set((state) => ({
      inventories: state.inventories.map((inventory) => {
        if (inventory.id === inventoryId) {
          return {
            ...inventory,
            variations: inventory.variations.map((variation: { id: string }) =>
              variation.id === variationId ? { ...variation, ...updatedFields } : variation,
            ),
          };
        }
        return inventory;
      }),
    }));
  },
  createVariation: async (data: {variantName: string, types: string[]}) => {
    set({isLoading: true})
    try {
      const res = await inventoryService.post("/inventories/variant", data)
     
     } catch (e:any) {
       set({ isLoading: false, error: e.message });
     }
  },
  removeVariation: (inventoryId, variationId) => {
    set((state) => ({
      inventories: state.inventories.map((inventory) => {
        if (inventory.id === inventoryId) {
          return {
            ...inventory,
            variations: inventory.variations.filter((variation: { id: string }) => variation.id !== variationId),
          };
        }
        return inventory;
      }),
    }));
  },

  fetchInventoryById: async (id: string) => {
    set((prevState: InventoryStore) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      const res = await inventoryService.get(`/inventories/${id}`);
      set((prevState: InventoryStore) => ({
        ...prevState,
        inventory: res.data.data,
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      set((prevState: InventoryStore) => ({
        ...prevState,
        isLoading: false,
        error: e.message,
      }));
    }
  },
}));
